import { Box, IconButton, Menu, MenuItem, styled, Typography } from '@mui/material'
import { CustomDivider } from 'src/components/batch-numbers/BatchNumberCell'
import { getThemeColor } from 'src/utils'
import { MUIThemeColors } from 'src/mui-theme'
import React, { useState } from 'react'

export const OverFlowButton = styled('span') <{ disabled?: boolean }>`
  height: 32px;
  display: flex;
  align-items: center;
  text-transform: capitalize;
  cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
  color: ${props =>
    props.disabled
      ? getThemeColor(props.theme, MUIThemeColors.secondaryDark)
      : getThemeColor(props.theme, MUIThemeColors.primaryMain)};
  opacity: ${props => (props.disabled ? 0.9 : 1)};
  display: inline-block;
  margin-top: 2px;
  padding: 6px 8px;
  transition: background 0.2s;
  border-radius: 4px;
  &:hover {
    background: ${props => (props.disabled ? 'none' : getThemeColor(props.theme, MUIThemeColors.secondaryMain))};
  }
`
export const StyledOverflowDiv = styled('div')<{ maxWidth: number }>(props => ({
  fontSize: 'inherit',
  maxWidth: `${props.maxWidth}px`,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: 'inline-block',
}))
interface OverFlowMenuProps {
  visibleCount?: number
  items: Array<{
    id: number
    name: string
    [key: string]: any
  }>
  enableMoreOption?: boolean
  actionIcon?: React.ReactNode
  onAction?: (id: number, item: any) => void
  renderItemName?: (item: any) => string
  itemDataTestId?: string
}

export const OverFlowMenu = ({ visibleCount, items, enableMoreOption = true, actionIcon, onAction, renderItemName, itemDataTestId }: OverFlowMenuProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setAnchorEl(e.currentTarget)
  }

  const handleCloseDropdown = () => {
    setAnchorEl(null)
  }

  const getItemDisplayName = (item: any) => {
    if (renderItemName) {
      return renderItemName(item)
    }
    return item.name
  }

  const renderItemContent = (item: any) => (
    <Box
      display={'flex'}
      flexDirection={'row'}
      alignContent={'center'}
      alignItems={'center'}
      justifyContent={'space-between'}
      width={'100%'}
    >
      <Typography>{getItemDisplayName(item)}</Typography>
      {actionIcon && onAction && (
        <IconButton
          onClick={e => {
            e.stopPropagation()
            onAction(item.id, item)
          }}
        >
          {actionIcon}
        </IconButton>
      )}
    </Box>
  )

  const renderMenuItems = (itemsToRender: any[]) =>
    itemsToRender.map((item, index) => (
      <MenuItem key={index}>
        {renderItemContent(item)}
      </MenuItem>
    ))

  // Show all items directly without "More" option
  if (!enableMoreOption) {
    return (
      <Box sx={{ minWidth: 280 }}>
        {items.map((item, index) => (
          <MenuItem key={index} onClick={() => { }} data-test-id={itemDataTestId}>
            {renderItemContent(item)}
          </MenuItem>
        ))}
      </Box>
    )
  }

  return (
    <Box>
      <Box display={'flex'} flexDirection={'row'} alignItems={'center'}>
        {items.slice(0, visibleCount).map((item, index) => (
          <>
            {renderItemContent(item)}
            {items.slice(index + 1, items.length).length > 0 ? <CustomDivider /> : null}
          </>
        ))}
        {items.slice(visibleCount, items.length).length > 0 ? (
          <Box>
            <OverFlowButton onClick={e => handleClick(e)}>
              <StyledOverflowDiv maxWidth={60}>More</StyledOverflowDiv>
            </OverFlowButton>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleCloseDropdown}>
              {renderMenuItems(items.slice(visibleCount))}
            </Menu>
          </Box>
        ) : null}
      </Box>
    </Box>
  )
}
